import { yupResolver } from '@hookform/resolvers/yup'
import { Box, Chip, Grid, Typography, makeStyles } from '@material-ui/core'
import VisibilityOutlined from '@material-ui/icons/VisibilityOutlined'
import Alert from '@material-ui/lab/Alert'
import moment from 'moment'
import React, { useEffect, useRef, useState } from 'react'
import { useForm } from 'react-hook-form'
import { ObterCep } from 'src/api/cep/cep'
import {
  CampoRegularizacao,
  FormularioRegularizacaoResponse
} from 'src/api/recebedor-pendencia-bacen'
import Button from 'src/components/ui/Button'
import yup from 'src/components/yupCustom'
import { onlyNumber } from 'src/core/helpers/common'
import AvisoBacen from './avisoBacen'
import CampoDinamico from './campoDinamico'
import ContaBancariaLeitura from './contaBancariaLeitura'
import {
  agruparCampos,
  camposExigemLayoutPj,
  chaveCampoFormulario,
  inferirTipoPessoa,
  isSiteUrlValida,
  larguraCampo,
  mensagemSiteUrlInvalida,
  rotuloTipoPessoa,
  rotuloCampo,
  tituloTipoRecebedor
} from './gruposCampos'

const useStyles = makeStyles(theme => ({
  erroGeral: {
    marginBottom: theme.spacing(2)
  },
  cabecalho: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: theme.spacing(2),
    flexWrap: 'wrap',
    marginBottom: theme.spacing(2),
    padding: theme.spacing(2, 3),
    border: `1px solid ${theme.palette.grey[300]}`,
    borderRadius: 8,
    backgroundColor: theme.palette.grey[50]
  },
  nome: {
    fontSize: '1.5rem',
    fontWeight: 600,
    color: '#455890',
    lineHeight: 1.3,
    margin: 0
  },
  chips: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(1),
    marginTop: theme.spacing(1)
  },
  chipTipo: {
    height: 28,
    fontWeight: 600,
    '&.MuiChip-root': {
      backgroundColor: '#EEEEEE',
      color: '#424242'
    }
  },
  chipPf: {
    height: 28,
    fontWeight: 600,
    '&.MuiChip-root': {
      backgroundColor: '#E3F2FD',
      color: '#1565C0'
    }
  },
  chipPj: {
    height: 28,
    fontWeight: 600,
    '&.MuiChip-root': {
      backgroundColor: '#F3E5F5',
      color: '#6A1B9A'
    }
  },
  secao: {
    marginBottom: theme.spacing(3),
    padding: theme.spacing(2, 3),
    border: `1px solid ${theme.palette.grey[300]}`,
    borderRadius: 8,
    backgroundColor: '#fff'
  },
  tituloSecao: {
    fontSize: 20,
    fontWeight: 500,
    color: '#455890',
    marginBottom: theme.spacing(1)
  }
}))

type Props = {
  formulario: FormularioRegularizacaoResponse
  enviando: boolean
  erroServidor: string | null
  onSubmit: (valores: Record<string, string>) => Promise<void>
  onVerInformacaoCompleta: () => void
}

function ehCampoEmail(campo: CampoRegularizacao) {
  const chave = campo.campo.split('.').pop() || campo.campo
  return chave.toLowerCase() === 'email'
}

function montarSchema(campos: CampoRegularizacao[]) {
  const shape: Record<string, any> = {}

  campos.forEach(campo => {
    const chave = chaveCampoFormulario(campo.campo)

    if (campo.tipo === 'confirmacao') {
      shape[chave] = yup
        .string()
        .required()
        .oneOf(['true'], 'Confirme para continuar')
        .label(rotuloCampo(campo))
      return
    }

    if (campo.tipo === 'moeda') {
      shape[chave] = yup
        .string()
        .required()
        .test('moeda', 'Informe um valor maior que zero', value => {
          const n = Number(value)
          return !Number.isNaN(n) && n > 0
        })
        .label(rotuloCampo(campo))
      return
    }

    if (campo.tipo === 'url') {
      shape[chave] = yup
        .string()
        .required()
        .test('url-https', mensagemSiteUrlInvalida(), value =>
          isSiteUrlValida(String(value || ''))
        )
        .label(rotuloCampo(campo))
      return
    }

    if (campo.tipo === 'data') {
      shape[chave] = yup
        .string()
        .required()
        .test('data', 'Informe uma data válida', value => {
          if (!value || String(value).replace(' ', '').length < 10) return false
          // DateInput grava yyyy-MM-DD; aceita também dd/MM/yyyy por resiliência
          return moment(value, ['YYYY-MM-DD', 'DD/MM/YYYY'], true).isValid()
        })
        .label(rotuloCampo(campo))
      return
    }

    if (campo.tipo === 'telefone') {
      shape[chave] = yup
        .string()
        .required()
        .test('telefone', 'Telefone inválido', value => {
          const digitos = onlyNumber(String(value || ''))
          return digitos.length === 10 || digitos.length === 11
        })
        .label(rotuloCampo(campo))
      return
    }

    if (ehCampoEmail(campo)) {
      shape[chave] = yup
        .string()
        .required()
        .email('Informe um e-mail válido')
        .label(rotuloCampo(campo))
      return
    }

    shape[chave] = yup.string().required().label(rotuloCampo(campo))
  })

  return yup.object().shape(shape)
}

function valoresIniciais(campos: CampoRegularizacao[]) {
  const valores: Record<string, any> = {}
  campos.forEach(campo => {
    valores[chaveCampoFormulario(campo.campo)] =
      campo.tipo === 'moeda' ? '0' : ''
  })
  return valores
}

function lerValorFormulario(valores: Record<string, any>, caminho: string) {
  if (
    valores != null &&
    Object.prototype.hasOwnProperty.call(valores, caminho) &&
    typeof valores[caminho] !== 'object'
  ) {
    return valores[caminho]
  }

  return caminho.split('.').reduce((acc: any, parte) => {
    if (acc == null) return undefined
    const indice = /^(.+)\[(\d+)\]$/.exec(parte)
    if (indice) {
      const lista = acc[indice[1]]
      return Array.isArray(lista) ? lista[Number(indice[2])] : undefined
    }
    return acc[parte]
  }, valores)
}

const MAPA_VIA_CEP: Record<string, (data: any) => string | undefined> = {
  city: data => data.localidade,
  street: data => data.logradouro,
  state: data => (data.uf ? String(data.uf).toUpperCase() : undefined),
  neighborhood: data =>
    data.bairro ? String(data.bairro).toUpperCase() : undefined
}

export default function FormularioRegularizacao({
  formulario,
  enviando,
  erroServidor,
  onSubmit,
  onVerInformacaoCompleta
}: Props) {
  const classes = useStyles()
  const primeiroErroRef = useRef<HTMLDivElement | null>(null)
  const tipoPessoa = inferirTipoPessoa(formulario)
  const campos = formulario.campos || []
  // O documento da conta pode ser CPF enquanto o cadastro na Pagar.me é PJ.
  // O layout segue os campos pendentes; o chip continua o tipo do documento.
  const ehPj = tipoPessoa === 'PJ' || camposExigemLayoutPj(campos)
  const grupos = agruparCampos(campos, ehPj)
  const tipoRecebedor = tituloTipoRecebedor(
    formulario.tipoRecebedor,
    formulario.subtipoParceiro
  )
  const labelPessoa = rotuloTipoPessoa(tipoPessoa)

  const { control, handleSubmit, errors, setValue, watch, register } = useForm({
    mode: 'all',
    resolver: yupResolver(montarSchema(formulario.campos || [])),
    defaultValues: valoresIniciais(formulario.campos || [])
  })

  // Assina o form inteiro para o setValue do ViaCEP refletir nos inputs controlados
  watch()

  const [loadingCep, setLoadingCep] = useState<Record<string, boolean>>({})
  const ultimoCepRef = useRef<Record<string, string>>({})

  // RHF v6: setValue só atualiza inputs registrados
  useEffect(() => {
    ;(formulario.campos || []).forEach(campo => {
      register({ name: chaveCampoFormulario(campo.campo) })
    })
  }, [formulario.campos, register])

  async function buscarCep(campoCep: string, cep: string) {
    const cepNumeros = onlyNumber(String(cep || ''))
    if (cepNumeros.length !== 8) return
    if (ultimoCepRef.current[campoCep] === cepNumeros) return
    ultimoCepRef.current[campoCep] = cepNumeros

    const prefixo = campoCep.replace(/\.zip_code$/i, '')

    try {
      setLoadingCep(atual => ({ ...atual, [campoCep]: true }))
      const { data } = await ObterCep(cepNumeros)
      if (!data || (data as { erro?: boolean }).erro) return
      ;(formulario.campos || []).forEach(campo => {
        if (!campo.campo.startsWith(`${prefixo}.`)) return
        const sufixo = campo.campo.split('.').pop() || ''
        const valor = MAPA_VIA_CEP[sufixo]?.(data)
        if (!valor) return
        setValue(chaveCampoFormulario(campo.campo), valor, {
          shouldValidate: true,
          shouldDirty: true
        })
      })
    } catch {
      ultimoCepRef.current[campoCep] = ''
    } finally {
      setLoadingCep(atual => ({ ...atual, [campoCep]: false }))
    }
  }

  async function enviar(valores: Record<string, any>) {
    const payload: Record<string, string> = {}
    formulario.campos.forEach(campo => {
      const chave = chaveCampoFormulario(campo.campo)
      const bruto = valores[chave] ?? lerValorFormulario(valores, campo.campo)
      if (bruto === undefined || bruto === null || bruto === '') {
        payload[campo.campo] = ''
        return
      }
      // Pagar.me / merge esperam dd/MM/yyyy; DateInput mantém yyyy-MM-DD no form
      if (campo.tipo === 'data') {
        const data = moment(String(bruto), ['YYYY-MM-DD', 'DD/MM/YYYY'], true)
        payload[campo.campo] = data.isValid()
          ? data.format('DD/MM/YYYY')
          : String(bruto)
        return
      }
      if (campo.tipo === 'url') {
        payload[campo.campo] = String(bruto).trim()
        return
      }
      payload[campo.campo] = String(bruto)
    })
    await onSubmit(payload)
  }

  function onInvalid() {
    if (primeiroErroRef.current) {
      primeiroErroRef.current.focus()
      primeiroErroRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'center'
      })
    }
  }

  return (
    <form noValidate onSubmit={handleSubmit(enviar, onInvalid)}>
      <Box className={classes.cabecalho}>
        <Box>
          <Typography className={classes.nome} component="h2">
            {formulario.nome}
          </Typography>
          <Box className={classes.chips}>
            {tipoRecebedor ? (
              <Chip
                size="small"
                label={tipoRecebedor}
                className={classes.chipTipo}
              />
            ) : null}
            {labelPessoa ? (
              <Chip
                size="small"
                label={labelPessoa}
                className={
                  tipoPessoa === 'PJ' ? classes.chipPj : classes.chipPf
                }
              />
            ) : null}
          </Box>
        </Box>
        <Button
          type="button"
          variant="contained"
          color="primary"
          name="ver-informacao-completa"
          startIcon={<VisibilityOutlined />}
          onClick={onVerInformacaoCompleta}
          aria-haspopup="dialog"
        >
          Ver informação completa
        </Button>
      </Box>

      <AvisoBacen dataLimite={formulario.dataLimite} />

      <ContaBancariaLeitura conta={formulario.contaBancaria} />

      {erroServidor && (
        <Alert className={classes.erroGeral} severity="error" role="alert">
          {erroServidor}
        </Alert>
      )}

      <div ref={primeiroErroRef} tabIndex={-1}>
        {grupos.length === 0 && (
          <Alert className={classes.erroGeral} severity="info">
            O representante legal já está cadastrado. Envie para concluir a
            regularização.
          </Alert>
        )}
        {grupos.map(grupo => (
          <Box
            key={grupo.id}
            className={classes.secao}
            component="section"
            aria-label={grupo.titulo}
          >
            <Typography className={classes.tituloSecao} component="h3">
              {grupo.titulo}
            </Typography>
            <Grid container spacing={2} alignItems="flex-start">
              {grupo.campos.map(campo => (
                <Grid item key={campo.campo} {...larguraCampo(campo)}>
                  <CampoDinamico
                    campo={campo}
                    pendenciaId={formulario.pendenciaId}
                    control={control}
                    errors={errors}
                    setValue={setValue}
                    watch={watch}
                    loading={!!loadingCep[campo.campo]}
                    onCepChange={buscarCep}
                  />
                </Grid>
              ))}
            </Grid>
          </Box>
        ))}
      </div>

      <Box mt={2}>
        <Button
          type="submit"
          variant="contained"
          color="primary"
          disabled={enviando}
          loading={enviando}
        >
          Enviar regularização
        </Button>
      </Box>
    </form>
  )
}
